import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Button as MuiButton } from "@mui/material";
import { DatePicker, Button, Select, Spin, Table } from "antd";
import { CloseIcon, DropDown, BackArrowIcon } from "../../assets/icons";
import { getWorkerHoursReport, getWorkersData } from "../../services/service";
import { CustomToast } from "../../atoms/toastMessage";
import { exportWorkerHoursToPDF } from "../../utils/exportUtils";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

const formatHHMMSSToHm = (hhmmss) => {
  const parts = String(hhmmss || "0:0:0").split(":").map(Number);
  const [hours = 0, minutes = 0] = parts;
  return `${hours}h ${minutes}m`;
};

const WorkerHoursReport = ({ open, onClose, preSelectedWorkerId = null, preSelectedWorkerIds = null, heading = "Worker Hours Report", projectId = null }) => {
  const getInitialWorkers = () =>
    preSelectedWorkerIds?.length ? preSelectedWorkerIds : (preSelectedWorkerId ? [preSelectedWorkerId] : []);

  const [dateRange, setDateRange] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState(getInitialWorkers());
  const [reportData, setReportData] = useState([]);
  const [workerTotals, setWorkerTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [viewingWorker, setViewingWorker] = useState(null);

  // Fetch workers when modal opens
  useEffect(() => {
    if (open) {   
      const fetchWorkers = async () => {
        setWorkersLoading(true);
        try {
          const response = await getWorkersData("", 100, 1, "");
          if (response?.success) {
            setWorkers(response?.data || []);
          }
        } catch (error) {
          console.error("Error fetching workers:", error);
        } finally {
          setWorkersLoading(false);
        }
      };
      fetchWorkers();
    }
  }, [open]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedWorkers(getInitialWorkers());
      setDateRange(null);
      setReportData([]);
      setWorkerTotals([]);
      setHasSearched(false);
      setViewingWorker(null);
    }
  }, [open, preSelectedWorkerId, preSelectedWorkerIds]);

  const getTimeRangeLabel = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      return `${dateRange[0].format("MMM DD, YYYY")} - ${dateRange[1].format("MMM DD, YYYY")}`;
    }
    return "All Time";
  };

  const handleGenerateReport = async () => {
    if (selectedWorkers.length === 0) {
      CustomToast({
        type: "error",
        message: "Please select at least one worker",
      });
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setViewingWorker(null);
    try {
      const startDate = dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "";
      const endDate = dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "";

      const response = await getWorkerHoursReport({
        worker_ids: selectedWorkers,
        start_date: startDate,
        end_date: endDate,
        project_id: projectId || "",
      });

      const totalsRoot = response?.workers || response?.startDate || response?.start_date
        ? response
        : (response?.data && typeof response.data === 'object' ? response.data : response);
      const workerTotalsList = (Array.isArray(totalsRoot?.workers) ? totalsRoot.workers : []).map((w) => ({
        workerId: w?.worker?.id,
        workerName: w?.worker?.username || w?.worker?.name || "",
        workingHours: w?.totals?.workingHours || "00:00:00",
        breakTime: w?.totals?.breakTime || "00:00:00",
        travelTime: w?.totals?.travelTime || "00:00:00",
      }));
      setWorkerTotals(workerTotalsList);

      // Transform API response into table rows
      const transformReportApiData = (raw, selectedStartDate, selectedEndDate) => {
        if (!raw) return [];

        // If it's already an array of rows, pass through
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw?.data)) return raw.data;

        // Normalize root in case API wraps payload inside data
        const root = raw?.workers || raw?.startDate || raw?.start_date
          ? raw
          : (raw?.data && typeof raw.data === 'object' ? raw.data : raw);

        // Expected shape based on provided sample
        // {
        //   startDate, endDate, totalWorkers, workers: [{ worker: {id,name,...}, dailyReports: [...]}]
        // }
        const workersArr = Array.isArray(root?.workers) ? root.workers : [];
        const rows = [];
        const rangeStart = moment(selectedStartDate, "YYYY-MM-DD", true);
        const rangeEnd = moment(selectedEndDate, "YYYY-MM-DD", true);
        const rangeDates = [];
        if (rangeStart.isValid() && rangeEnd.isValid() && !rangeStart.isAfter(rangeEnd, "day")) {
          const cursor = rangeStart.clone();
          while (!cursor.isAfter(rangeEnd, "day")) {
            rangeDates.push(cursor.format("YYYY-MM-DD"));
            cursor.add(1, "day");
          }
        }

        const toEpoch = (value, fallbackDate = null) => {
          if (!value) return null;

          // Handle ISO-like timestamps
          if (typeof value === "string" && (value.includes("T") || value.endsWith("Z"))) {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
          }

          // Handle time-only strings by combining with report date
          if (typeof value === "string") {
            const datePart = fallbackDate || moment().format("YYYY-MM-DD");
            const timeMoment = moment(
              `${datePart} ${value}`,
              ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"],
              true
            );
            if (timeMoment.isValid()) return timeMoment.valueOf();

            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
          }

          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
        };

        const getLatestCheckout = (dayRow) => {
          const checkoutCandidates = [
            dayRow?.lastCheckOutTime,
            dayRow?.lastCheckoutTime,
            dayRow?.checkOut,
            dayRow?.checkout,
            dayRow?.checkOutTime,
          ].filter(Boolean);

          if (!checkoutCandidates.length) return null;

          const latest = checkoutCandidates.reduce((latestValue, currentValue) => {
            const latestEpoch = toEpoch(latestValue, dayRow?.date);
            const currentEpoch = toEpoch(currentValue, dayRow?.date);

            if (currentEpoch == null) return latestValue;
            if (latestEpoch == null) return currentValue;
            return currentEpoch > latestEpoch ? currentValue : latestValue;
          }, checkoutCandidates[0]);

          return latest || null;
        };

        workersArr.forEach((w) => {
          const workerName = w?.worker?.username || w?.worker?.name || "";
          const workerId = w?.worker?.id;
          const daily = Array.isArray(w?.dailyReports) ? w.dailyReports : [];
          const dailyByDate = new Map();

          daily.forEach((d) => {
            const rowDate = moment(d?.date, ["YYYY-MM-DD", moment.ISO_8601], true);
            if (!rowDate.isValid()) return;
            const dateKey = rowDate.format("YYYY-MM-DD");
            if (rangeDates.length && (rowDate.isBefore(rangeStart, "day") || rowDate.isAfter(rangeEnd, "day"))) {
              return;
            }

            const inTime = d?.checkInTime ?? d?.checkIn ?? null;
            const outTime = getLatestCheckout(d);
            const normalizedRow = {
              workerId,
              workerName,
              date: dateKey,
              localTimezone: d?.local_timezone || w?.worker?.local_timezone || null,
              checkInTime: inTime,
              checkOutTime: outTime,
              totalWorkingTime: d?.workingHours ?? "00:00",
              totalBreakTime: d?.breakTime ?? "00:00",
              totalTravelTime: d?.travelTime ?? "00:00",
            };

            const existing = dailyByDate.get(dateKey);
            if (!existing) {
              dailyByDate.set(dateKey, normalizedRow);
              return;
            }

            const existingOut = toEpoch(existing.checkOutTime, dateKey);
            const currentOut = toEpoch(normalizedRow.checkOutTime, dateKey);
            if (currentOut != null && (existingOut == null || currentOut > existingOut)) {
              dailyByDate.set(dateKey, normalizedRow);
            }
          });

          const datesToRender = rangeDates.length
            ? rangeDates
            : Array.from(dailyByDate.keys()).sort((a, b) => new Date(a) - new Date(b));

          datesToRender.forEach((dateKey) => {
            const existingRow = dailyByDate.get(dateKey);
            if (existingRow) {
              rows.push(existingRow);
              return;
            }

            rows.push({
              workerId,
              workerName,
              date: dateKey,
              localTimezone: w?.worker?.local_timezone || null,
              checkInTime: "-",
              checkOutTime: "-",
              totalWorkingTime: "-",
              totalBreakTime: "-",
              totalTravelTime: "-",
            });
          });
        });
        return rows;
      };

      const normalized = transformReportApiData(response, startDate, endDate);
      setReportData(normalized);
      if (normalized.length === 0) {
        const root = response?.workers || response?.startDate || response?.start_date
          ? response
          : (response?.data && typeof response.data === 'object' ? response.data : response);
        const hasWorkersMeta = Array.isArray(root?.workers) && root.workers.length > 0;
        if (!hasWorkersMeta) {
          CustomToast({
            type: "info",
            message: "No data found for the selected criteria",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      CustomToast({
        type: "error",
        message: error?.response?.data?.message || "Failed to fetch report data",
      });
      setReportData([]);
      setWorkerTotals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const dataToExport = viewingWorker
      ? reportData.filter((row) => row.workerId === viewingWorker.id)
      : reportData;

    if (dataToExport.length === 0) {
      CustomToast({
        type: "error",
        message: "No data to export",
      });
      return;
    }

    const reportDates = dataToExport.map((row) => row.date).filter(Boolean).sort();
    const startDate = dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : (reportDates[0] || "All time");
    const endDate = dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : (reportDates[reportDates.length - 1] || "All time");

    exportWorkerHoursToPDF(dataToExport, startDate, endDate);
    CustomToast({
      type: "success",
      message: "Report exported successfully",
    });
  };

  const summaryColumns = [
    {
      title: "Worker Name",
      dataIndex: "workerName",
      key: "workerName",
    },
    {
      title: "Time Range",
      key: "timeRange",
      render: () => getTimeRangeLabel(),
    },
    {
      title: "Working Hours",
      dataIndex: "workingHours",
      key: "workingHours",
      render: (value) => formatHHMMSSToHm(value),
    },
    {
      title: "",
      key: "viewDetails",
      width: 120,
      render: (_, record) => (
        <button
          type="button"
          className="text-[#F97316] font-medium text-[13px] hover:underline"
          onClick={() => setViewingWorker({ id: record.workerId, name: record.workerName })}
        >
          View Details
        </button>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (text) => {
        const m = moment(text, ["YYYY-MM-DD", moment.ISO_8601], true);
        return m.isValid() ? m.format("MMM DD, YYYY") : (text || "N/A");
      },
    },
    {
      title: "Check In",
      dataIndex: "checkInTime",
      key: "checkInTime",
      width: 120,
      render: (text, record) => {
        // Handle already formatted times like "04:00 AM" as pass-through
        if (!text || text === "-") return "-";
        // If ISO-like, format in activity's local timezone
        if (typeof text === 'string' && (text.includes('T') || text.endsWith('Z'))) {
          const parsed = new Date(text);
          if (!Number.isNaN(parsed.getTime())) {
            try {
              return new Intl.DateTimeFormat("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone:
                  record?.localTimezone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              }).format(parsed);
            } catch (_) {}
          }
          return text;
        }
        const m = moment(text, ["HH:mm", "hh:mm A", moment.ISO_8601], true);
        return m.isValid() ? m.format("hh:mm A") : text;
      },
    },
    {
      title: "Check Out",
      dataIndex: "checkOutTime",
      key: "checkOutTime",
      width: 120,
      render: (text, record) => {
        if (!text || text === "-") return "-";
        if (typeof text === 'string' && (text.includes('T') || text.endsWith('Z'))) {
          const parsed = new Date(text);
          if (!Number.isNaN(parsed.getTime())) {
            try {
              return new Intl.DateTimeFormat("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone:
                  record?.localTimezone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              }).format(parsed);
            } catch (_) {}
          }
          return text;
        }
        const m = moment(text, ["HH:mm", "hh:mm A", moment.ISO_8601], true);
        return m.isValid() ? m.format("hh:mm A") : text;
      },
    },
    {
      title: "Working Hours",
      dataIndex: "totalWorkingTime",
      key: "totalWorkingTime",
      width: 120,
    },
    {
      title: "Break Time",
      dataIndex: "totalBreakTime",
      key: "totalBreakTime",
      width: 120,
    },
    {
      title: "Travel Time",
      dataIndex: "totalTravelTime",
      key: "totalTravelTime",
      width: 120,
    },
  ];

  // Disable dates after today
  const disabledDate = (current) => {
    return current && current > moment().endOf('day');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      className="!overflow-visible"
      sx={{
        "& .MuiDialog-paperWidthLg": {
          overflow: "visible",
          maxWidth: "900px",
          maxHeight: "85vh",
        },
      }}
      style={{ marginTop: "2rem" }}
    >
      <DialogContent
        className="relative bg-white !p-0 rounded-lg shadow-md !overflow-visible flex flex-col"
        sx={{ maxHeight: "85vh" }}
      >
        {open && (
          <button
            type="button"
            aria-label="Close"
            className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-pointer z-50 p-0 rounded-full focus:outline-none"
            onClick={onClose}
          >
            {CloseIcon}
          </button>
        )}

        {/* Fixed header: title + filters */}
        <div className="flex-shrink-0 flex flex-col gap-4 px-6 pt-8 pb-4">
          <h2 className="font-semibold text-[24px] text-[#1E3A8A] text-center">
            {heading}
          </h2>

          <div className="flex gap-3">
            <div className="flex-1 flex flex-col gap-2">
              <p className="font-medium text-[14px] text-[#4B5563]">Date range (leave empty for all time)</p>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="YYYY-MM-DD"
                className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9]"
                disabledDate={disabledDate}
                placeholder={["", ""]}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </div>

            <div className="flex-1 flex flex-col gap-2">
              <p className="font-medium text-[14px] text-[#4B5563]">Worker</p>
              <Select
                mode="multiple"
                placeholder="Choose worker"
                value={selectedWorkers}
                onChange={(value) => setSelectedWorkers(value)}
                className="w-full min-h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
                style={{ paddingTop: '4px', paddingBottom: '4px' }}
                suffixIcon={DropDown}
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children || "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                maxTagCount="responsive"
                loading={workersLoading}
                getPopupContainer={(trigger) => trigger.parentElement}
              >
                {workers
                  .slice()
                  .sort((a, b) =>
                    (a.username || "").localeCompare(b.username || "")
                  )
                  .map((worker) => (
                    <Option key={worker.id} value={worker.id}>
                      {worker.username || worker.name || `Worker ${worker.id}`}
                    </Option>
                  ))}
              </Select>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="primary"
              onClick={handleGenerateReport}
              loading={loading}
              className="bg-[#F97316] border-none h-[44px] px-8 font-medium"
              style={{
                backgroundColor: '#F97316',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F97316';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#F97316';
              }}
            >
              Generate Report
            </Button>
          </div>
        </div>

        {/* Scrollable body: table only */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-2 pb-6">
          {viewingWorker && (
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setViewingWorker(null)}
                className="flex items-center gap-2 text-[#1E3A8A] font-medium text-[14px]"
              >
                {BackArrowIcon}
                Back
              </button>
              <span className="text-[14px] text-[#4B5563]">
                {viewingWorker.name} — {getTimeRangeLabel()}
              </span>
            </div>
          )}
          {reportData.length > 0 && (
            <div className="flex justify-end mb-3">
              <Button
                onClick={handleExportPDF}
                className="border-[#F97316] text-[#F97316] h-[36px] px-6 font-medium"
                style={{
                  borderColor: '#F97316',
                  color: '#F97316',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#F97316';
                  e.currentTarget.style.color = '#F97316';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#F97316';
                  e.currentTarget.style.color = '#F97316';
                }}
              >
                Export to PDF
              </Button>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center h-[300px]">
              <Spin size="large" />
            </div>
          ) : hasSearched && viewingWorker ? (
            <Table
              columns={detailColumns}
              dataSource={reportData.filter((row) => row.workerId === viewingWorker.id)}
              size="small"
              rowKey={(record) => `${record.workerId}-${record.date}`}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
              }}
              scroll={{ x: 700 }}
              locale={{
                emptyText: "No data found for the selected criteria",
              }}
            />
          ) : hasSearched ? (
            <Table
              columns={summaryColumns}
              dataSource={workerTotals}
              size="small"
              rowKey={(record) => record.workerId}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
              }}
              locale={{
                emptyText: "No data found for the selected criteria",
              }}
            />
          ) : (
            <div className="flex justify-center items-center h-[300px] text-gray-500">
              Select date range and workers, then click "Generate Report"
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkerHoursReport;
